import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useProfile } from "../components/ProfileContext";
import { SimpleFormField } from "../components/SimpleFormField";
import { Inspection } from "../models/Inspection";
import { Api } from "../util/Api";
import { LoadingComponent } from "../components/LoadingComponent";
import { parsePossibleMcElroyQrCode, setNavAdvisoryLockout } from "../util/utils";
import { InspectionsTable } from "../forms/InspectionsTable";
import { Survey } from "../models/Survey";
import {GroupedObjectSelector} from "../components/GroupedObjectSelector"
import {SimpleFormFieldState} from "../components/SimpleFormFieldState"
import { Link } from "react-router-dom";

export function InspectionBeginPage(props) {
	setNavAdvisoryLockout(false);

	let navigate = useNavigate();
	let [profile] = useProfile();

	// General Page State Values
	let [inInitialLookup, setInInitialLookup] = useState(true);
	let [searching, setSearching] = useState(false);
	let [searchResults, setSearchResults] = useState(null);

	// Values for the second form
	let [surveyList, setSurveyList] = useState([]);
	let groupedSurveys = Survey.groupSurveysByCategory(surveyList);
	let [surveyId, setSurveyId] = useState(null);
	let [assetNumber, setAssetNumber] = useState('');
	let [assetDescription, setAssetDescription] = useState('');
	let [heading, setHeading] = useState("Begin Inspection");
	// The scanned model and serial numbers, if any
	const [scannedModelNumber, setScannedModelNumber] = useState("");
	const [scannedSerialNumber, setScannedSerialNumber] = useState("");
	let [serverComponentInfo, setServerComponentInfo] = useState(null);

	useEffect(() => {console.log("insideUseEffect")}, [inInitialLookup])

	// Input references
	let inputRef = useRef();


	let [componentTypeInput, setComponentTypeInput] = useState(new Map());
	// This maps component_type ids to an object that maps hasModelNumber, hasSerialNumber, and name
	// Should look like this:
	// componentTypeInput = {
	// 	"id1": {
	// 		"name": "has_serial_and_model",
	// 		"hasSerialNumber": true,
	// 		"hasModelNumber": true
	// 	},
	// 	"id2": {
	// 		"name": "has_serial",
	// 		"hasSerialNumber": true,
	// 		"hasModelNumber": false
	// 	},
	// 	"id3": {
	// 		"name": "has_model",
	// 		"hasSerialNumber": null,
	// 		"hasModelNumber": true
	// 	}
	// }
	
	useEffect(() => {
		let component_type_map = null;
		if(surveyId){
			// Position list is reversed because popping is much more efficient
			let positionList = surveyList.filter(x => x.id == surveyId)[0]?.surveyComponentTypes?.map(x => x.position).reverse()
			// Need to pop position list because we don't have indices
			component_type_map = surveyList.filter(x => x.id == surveyId)[0]?.surveyComponentTypes?.map(x => x.componentType)?.reduce((accum, x) => { accum[x.id] = {"name": x.name, "hasSerialNumber": x.hasSerialNumber, "hasModelNumber": x.hasModelNumber, "position":positionList?.pop() }; return accum}, {});
		}
		let inputMap = {};
		if(component_type_map){
			// If we scanned an official McElroy QR code, we only want to fill in the first component's values
			const firstPosition = Math.min(...Object.values(component_type_map).map(x => x.position));

			for( const [key, val] of Object.entries(component_type_map)) {
				let tmpSerial = "";
				let tmpModel = "";
				let relevantServerComponent = serverComponentInfo == null ? null : serverComponentInfo.components.filter(x => x.componentTypeId == key)[0];
				if(relevantServerComponent != null) {
					tmpSerial = relevantServerComponent.serialNumber;
					tmpModel = relevantServerComponent.modelNumber;
				} else {
					if(val.position == firstPosition) {
						tmpSerial = scannedSerialNumber;
						tmpModel = scannedModelNumber;
					}
				}

				inputMap[key] = {serial: tmpSerial, model: tmpModel, name: val.name, position: val.position}
			}
		
			let keyList = Object.keys(component_type_map);
			keyList.sort((a, b) => component_type_map[a].position - component_type_map[b].position);

			// Let's sort everything by the specified position
			let sortedInputMap = new Map();

			for(const key of keyList.values()){
				sortedInputMap[key] = component_type_map[key];
			}
			setComponentTypeInput(inputMap);
		}
	}, [surveyId, surveyList, serverComponentInfo]);

	let allowedStartInspection = profile.isAllowedToStartInspection;
	let canStartInspection = surveyId != null && !!assetNumber;

	let readyNextScreenFromResults = async (defaultAssetNumber, results) => {
		let success = false;
		if(results != null) {
			let comp = results.component;
			if(comp != null) {
				success = true;
				let foundSurvey = await Survey.getActiveSurveyForComponentTypeId(comp.componentTypeId, comp.survey);

				setServerComponentInfo(comp);
				setSurveyId(foundSurvey?.id);
				setAssetNumber(comp.assetNumber);
				setAssetDescription(comp.assetDescription);
			}
		}

		// We didn't find anything, so we are going to assume that this is a new value
		if(!success) {
			setAssetNumber(defaultAssetNumber);
		}
		setSurveyList(await Survey.getActiveSurveys());
		setSearchResults(results);
		setSearching(false);		
		setInInitialLookup(false);
	};

	// https://www.mcelroy.com/lookup/?mn=themodel&sn=theserial
	let someNumberEntered = async () => {
		setHeading("Setup");
		let enteredValue = inputRef.current.value;
		if(!enteredValue) {
			return; // Don't try blanks
		}

		let results = null;
		setSearching(true);


		let parsedVal = parsePossibleMcElroyQrCode(enteredValue);
		if(parsedVal != null) { // Official McElroy QR
			try { results = await Api.forProfile(profile).componentLookupByModelSerial(parsedVal.mn, parsedVal.sn); } catch(err) { }
			readyNextScreenFromResults(null, results); // NOTE - Don't pre-populate asset number since this is *definitely* a model/serial combination
			setScannedModelNumber(parsedVal.mn);
			setScannedSerialNumber(parsedVal.sn);
			
		} else { // Asset Number Scan or Direct Entry
			try { results = await Api.forProfile(profile).componentLookupByUnique(enteredValue); } catch(err) { }
			readyNextScreenFromResults(enteredValue, results);
		}
	};

	let startInspectionAction = async () => {
		let insp = Inspection.generateInspectionFromProfile(profile);
		let survey = await Survey.find(surveyId);
		let tab = "survey";

		if(insp.rentalMode) {
			// Fill out additional customer fields
			tab = "setup";
		}

		insp.assetNumber = assetNumber;
		insp.assetDescription = assetDescription;
		
		for(let [componentTypeId, val] of Object.entries(componentTypeInput)) {
			insp.populateComponentData({
				"assetNumber": assetNumber,
				"modelNumber": val.model,
				"serialNumber": val.serial,
				"componentTypeId": componentTypeId
			})
		}

		// survey
		insp.surveyId = surveyId;

		navigate("/inspections/new", {state:{inspection:insp, startTab:tab}});
	};

	let returnToStart = () => {
		console.log("Inside return to Start");
		window.location.reload(false);
		// setInInitialLookup(false);
		// navigate("/inspections/begin");
	}


	let runPossibleMcElroyScan = (scanText, componentTypeId) => {
		let qrResult = parsePossibleMcElroyQrCode(scanText);
		if(qrResult == null) {
			return false; // Let it keep processing
		}
		

		let newInput =
		{
			...componentTypeInput,
			[componentTypeId]: {
				serial: qrResult.sn,
				model: qrResult.mn,
			}
		};
		setComponentTypeInput(newInput);
		return true; // Stop processing
	}

	let getComponentTypeForms = function() {
		let result_list = [];

		// Since componentTypeInput is an object, we'll need to put the keys in a list
		// so that we may display the component fields in the correct position
		let keyList = Object.keys(componentTypeInput);
		keyList.sort((a, b) => componentTypeInput[a].position - componentTypeInput[b].position);

		for(const key of keyList.values()) {
			let value = componentTypeInput[key];
			result_list.push(<Row key={key}>
				<Col sm={2}>{value.name}</Col>
				<Col><SimpleFormField key={key + "-model"} onChange={ (e) => {let x = {...componentTypeInput, [key]: {...value, "model": e.target.value }}; setComponentTypeInput( x )} } value={value?.model} color="#007041" scannable={true} onScan={ (text) => {runPossibleMcElroyScan(text, key)}} /></Col>
				<Col><SimpleFormField key={key + "-serial"} onChange={ (e) => {let x = {...componentTypeInput, [key]: {...value, "serial": e.target.value }}; setComponentTypeInput( x )} } value={value?.serial} color="#007041" scannable={true} onScan={ (text) => {runPossibleMcElroyScan(text, key)}} /></Col>
			</Row>);
		}
		return result_list;
	}
	let getComponentTypeFormsMobile = function() {
		let result_list = [];

		// Since componentTypeInput is an object, we'll need to put the keys in a list
		// so that we may display the component fields in the correct position
		let keyList = Object.keys(componentTypeInput);
		keyList.sort((a, b) => componentTypeInput[a].position - componentTypeInput[b].position);

		for(const key of keyList.values()) {
			let value = componentTypeInput[key];
			result_list.push(<Row key={key}>
				<label>Component: {value.name}</label>
				<label>Model Number</label>
				<SimpleFormField key={key + "-model"} onChange={ (e) => {let x = {...componentTypeInput, [key]: {...value, "model": e.target.value }}; setComponentTypeInput( x )} } value={value?.model} color="#007041" scannable={true} onScan={ (text) => {runPossibleMcElroyScan(text, key)}} />
				<label>Serial Number</label>
				<SimpleFormField key={key + "-serial"} onChange={ (e) => {let x = {...componentTypeInput, [key]: {...value, "serial": e.target.value }}; setComponentTypeInput( x )} } value={value?.serial} color="#007041" scannable={true} onScan={ (text) => {runPossibleMcElroyScan(text, key)}} />
			</Row>);
			result_list.push(<hr key={key + "_hr"}/>);
		}
		return result_list;
	}

	return <div className="startInspectionContainer">
		<h1>{heading}</h1>

		{ !allowedStartInspection && <p className="invalidId">
			{profile.isCurrentJobShared && <>
				You cannot create new inspections on a shared job.
			</>}
			{!profile.isCurrentJobShared && <>
				A valid ID for this inspector is required.  Please use the <Link to="/settings">Settings Screen</Link> to enter in your inspector ID.
			</>}
		</p>}
		{ allowedStartInspection && <>
			{inInitialLookup && <>
				<div>
					<SimpleFormField inputRef={inputRef} label="Enter/Scan Asset # or Nameplate" scannable={true} onScan={(text) => {setAssetNumber(text)} } afterScan={someNumberEntered} value={assetNumber} onChange={(e) => {setAssetNumber(e.target.value);}} />
				</div>
				<div>
					<Button className="startButton"  onClick={someNumberEntered}>Search</Button>
				</div>

				{searching && <LoadingComponent />}
			</>}

			{!inInitialLookup && <>
				{/* {setHeading("Setup")} */}
				<div>
					<FormGroup>
						<FormLabel style={{color:"#007041"}}> Asset Number (Required) </FormLabel>
						<SimpleFormFieldState scannable={false} value={assetNumber} setValue={setAssetNumber} id={"begin_asset"} name={"begin_asset"}></SimpleFormFieldState>
					</FormGroup>
					<FormGroup>
						<FormLabel style={{color:"#007041"}}> Asset Description </FormLabel>
						<SimpleFormFieldState scannable={false} value={assetDescription} setValue={setAssetDescription} id={"begin_asset_description"} name={"begin_asset_description"}></SimpleFormFieldState>
					</FormGroup>
					<FormGroup>
						<FormLabel style={{color:"#007041"}}>Equipment Type (Required)</FormLabel>
					</FormGroup>
					<GroupedObjectSelector defaultSelected={surveyId} groupedOptions={groupedSurveys} onChange={(e) => setSurveyId(e.target.value)}></GroupedObjectSelector>
				</div>
				<hr/>

				<Container className="d-none d-md-block" fluid="true">
					<Row>
						<Col style={{"color":"#007041"}} >Component</Col>
						<Col style={{"color":"#007041"}} >Model Number</Col>
						<Col style={{"color":"#007041"}} >Serial Number</Col>
					</Row>
					<hr/>
					{getComponentTypeForms()}
				</Container>

				<Container className="d-md-none" fluid="true">
					{getComponentTypeFormsMobile()}
				</Container>

				
				<div className="mt-4">
					<hr />
					<div className="buttonContainer">
						<Button id="btn" className="btn" onClick={returnToStart}>Back</Button>
						<Button className="btn continuebtn" onClick={startInspectionAction} disabled={!canStartInspection}>Continue</Button>
					</div>
					{searchResults != null && <div>
						<h2 className="mt-4 prevInspect">Previous Inspections</h2>
						{searchResults["inspections"].length == 0 && <p>No inspections found.</p>}
						{searchResults["inspections"].length > 0 && <div>
							<InspectionsTable inspections={searchResults["inspections"]} />
						</div>}
					</div>}
				</div>
			</>}
		</>}
	</div>;
}
